import React, { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import PaymentRequestButton from '../PaymentRequestButton';

interface WalletPaymentSectionProps {
    stripePromise: Promise<Stripe | null>;
    clientSecret: string;
    totalWithFee: number;
    movieTitle?: string;
    totalTickets: number;
    completed: boolean;
    setCompleted: (val: boolean) => void;
    handleBooking: (details: any) => Promise<void>;
}

export default function WalletPaymentSection({
    stripePromise,
    clientSecret,
    totalWithFee,
    movieTitle,
    totalTickets,
    completed,
    setCompleted,
    handleBooking
}: WalletPaymentSectionProps) {
    const [processingWallet, setProcessingWallet] = useState(false);

    return (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'night' } }}>
            <PaymentRequestButton
                amount={Math.round(totalWithFee * 100)}
                label={`${movieTitle || 'Movie'} - ${totalTickets} ticket${totalTickets > 1 ? 's' : ''}`}
                disabled={processingWallet || completed}
                onPaymentSuccess={async (pmId) => {
                    if (completed) return;
                    setProcessingWallet(true);
                    try {
                        // Confirm the payment with the payment method from wallet
                        const stripe = await stripePromise;
                        if (!stripe) throw new Error('Stripe not loaded');

                        const { error, paymentIntent } = await stripe.confirmCardPayment(
                            clientSecret,
                            {
                                payment_method: pmId
                            }
                        );

                        if (error) {
                            console.error('[Wallet] Payment error:', error);
                            window.alert(`Payment Error: ${error.message}`);
                            toast.error(error.message || 'Payment failed');
                            setProcessingWallet(false);
                            throw error;
                        }

                        console.log('[Wallet] Payment status:', paymentIntent?.status);

                        if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_capture')) {
                            console.log('[Wallet] ✅ Creating booking...');
                            setCompleted(true);

                            try {
                                await handleBooking({
                                    payment_intent_id: paymentIntent.id,
                                    amount: paymentIntent.amount || 0,
                                    status: paymentIntent.status
                                });
                                console.log('[Wallet] ✅ Booking complete!');
                            } catch (bookingErr: any) {
                                window.alert(`Booking Error: ${bookingErr.message}`);
                                toast.error(bookingErr.message);
                            }
                        } else {
                            window.alert(`Unexpected Status: ${paymentIntent?.status}`);
                        }
                    } catch (err: any) {
                        console.error('Wallet payment error:', err);
                        window.alert(`Exception: ${err.message}`);
                        toast.error(err.message || 'Payment failed');
                        throw err;
                    } finally {
                        setProcessingWallet(false);
                    }
                }}
                onPaymentError={(error) => {
                    window.alert(`Wallet Error: ${error}`);
                    toast.error(error);
                    setProcessingWallet(false);
                }}
            />
        </Elements>
    );
}
